import { DEFAULT_TIMEZONE, LOCIZE_NS } from '@avanta/shared/constants/common';
import { useHasVerticalOverflow } from '@avanta/shared/hooks/useHasVerticalOverflow';
import { cn } from '@avanta/shared/utils/cn';
import { useMemo, useRef } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { QUERY_KEYS } from 'constants/common';
import { ROUTES } from 'constants/routes';
import { useGetQuery } from 'hooks/useGetQuery';
import { useUserStore } from 'stores/user';
import { useTrackingEvents } from 'tracking/hooks/useTrackingEvents';
import { type Center, FLOW_MODES } from 'types/appointments';
import { getUserId } from 'utils/storage';
import { prepareAppointmentConfirmationData } from 'utils/tracking';
import { TopNav } from './components/ui/TopNav';
import { useBookAppointment } from './hooks/useBookAppointment';
import { useEditAppointment } from './hooks/useEditAppointment';
import { useStepNavigation } from './hooks/useStepNavigation';
import {
  AppointmentsReservePayload,
  defaultValues,
  type AppointmentsReserveFormData,
} from './types';

const AppointmentsReserveForm = (): React.ReactElement => {
  const { state } = useLocation();
  const { handleSubmit, watch } = useFormContext<AppointmentsReserveFormData>();
  const flow = watch(AppointmentsReservePayload.flow);
  const { steps, currentStep, currentStepIndex, handleNext, handleBack } = useStepNavigation();
  const nextStepName = steps[currentStepIndex + 1]?.name;
  const { mutate: bookAppointment } = useBookAppointment();
  const { mutate: editAppointment } = useEditAppointment();
  const StepComponent = currentStep.component;
  const specialityBooked = watch(AppointmentsReservePayload.specialityBooked);
  const { trackAppointmentConfirmation } = useTrackingEvents();
  const centersQuery = useGetQuery<Center[]>([QUERY_KEYS.FETCH_CENTERS, specialityBooked]);
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const isOutsideFlow = flow === FLOW_MODES.edit || flow === FLOW_MODES.recent_psychologist;
  const showMobileBack = currentStepIndex > 0 || flow === FLOW_MODES.edit;
  const navigate = useNavigate();

  const onSubmit = (data: AppointmentsReserveFormData): void => {
    if (!data.contactMethod || !data.psychologist) return;

    trackAppointmentConfirmation(
      prepareAppointmentConfirmationData(data, centersQuery?.data ?? [], id =>
        t(`AppointmentPdf.Speciality.options.${id}`)
      )
    );

    const patientId = state?.patientId ?? getUserId();

    if (flow === FLOW_MODES.edit) {
      editAppointment({
        patientId,
        psychologistId: data.psychologist.id,
        previousDate: state?.appointment.dateTime,
        newDate: data.dateTime,
        reason: data.observations,
      });
      return;
    }

    bookAppointment({
      patientId,
      specialityBooked: data.specialityBooked,
      backofficeCenterId: data.backofficeCenterId,
      contactMethod: data.contactMethod,
      dateTime: data.dateTime,
      psychologistId: data?.psychologist?.id,
      observations: data.observations,
      durationType: data.durationType,
    });
  };

  const handleOnBack = (index?: number): void => {
    if (isOutsideFlow && currentStepIndex === 0) {
      navigate(ROUTES.APPOINTMENTS);
      return;
    }
    const isAValidIndex = typeof index === 'number' && index >= 0 && index < steps.length;
    handleBack?.(isAValidIndex ? index : undefined);
  };

  const formScrollRef = useRef<HTMLFormElement>(null);
  const { hasOverflow, isAtTop } = useHasVerticalOverflow(formScrollRef, 'element', null);
  const showScrollShadow = hasOverflow && !isAtTop;

  return (
    <div className={cn('flex flex-col h-dvh overflow-hidden relative')}>
      <TopNav
        steps={steps}
        currentStepIndex={currentStepIndex + 1}
        onMobileBack={showMobileBack ? handleOnBack : undefined}
        showScrollShadow={showScrollShadow}
      />
      <form
        ref={formScrollRef}
        onSubmit={handleSubmit(onSubmit)}
        className="w-full overflow-y-auto h-full"
      >
        <StepComponent
          handleNext={handleNext}
          handleBack={handleOnBack}
          nextStepName={nextStepName}
        />
      </form>
    </div>
  );
};

export const AppointmentsReserve = (): React.ReactElement => {
  const { state } = useLocation();
  const { user } = useUserStore();
  const timezone = user?.timeZone ?? DEFAULT_TIMEZONE;
  const isOutsideFlow =
    state?.flow === FLOW_MODES.edit || state?.flow === FLOW_MODES.recent_psychologist;
  const initialValues = useMemo((): AppointmentsReserveFormData => {
    const initialValues = { ...defaultValues, timezone };

    if (state?.checkoutData) {
      return state.checkoutData;
    }
    if (isOutsideFlow) {
      return {
        ...initialValues,
        psychologist: state?.psychologist,
        backofficeCenterId: state?.appointment.center?.id,
        contactMethod: state?.appointment.bookedContactMethod,
        specialityBooked: state?.appointment.specialitiesBookable,
        flow: state?.flow,
      };
    }
    return initialValues;
  }, [state, timezone, isOutsideFlow]);

  const methods = useForm<AppointmentsReserveFormData>({
    defaultValues: initialValues,
  });
  return (
    <FormProvider {...methods}>
      <AppointmentsReserveForm />
    </FormProvider>
  );
};
